import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import {
  PortalLoadingCard,
  PortalMetricCard,
  PortalStateCard,
  formatPortalDate,
  getStatusChipStyle
} from './portalShared'

const useStyles = makeStyles((theme) => ({
  controlsCard: {
    borderRadius: 20,
    marginBottom: theme.spacing(3),
    boxShadow: '0 14px 30px rgba(31, 55, 42, 0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)'
  },
  tableCard: {
    borderRadius: 20,
    boxShadow: '0 14px 30px rgba(31, 55, 42, 0.08)',
    border: '1px solid rgba(205, 216, 209, 0.9)',
    overflow: 'hidden'
  },
  cardTitle: {
    fontWeight: 700,
    color: '#20342b'
  },
  helperText: {
    color: '#5d6c64'
  },
  countRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'flex-start'
    }
  },
  tableHeadCell: {
    fontWeight: 700,
    color: '#274237',
    backgroundColor: '#f6f8f6'
  },
  primaryText: {
    fontWeight: 700,
    color: '#20342b'
  },
  secondaryText: {
    color: '#5d6c64'
  },
  noteWrap: {
    maxWidth: 320
  },
  emptyCell: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  }
}))

const statusOptions = [
  { value: 'all', label: 'All statuses' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'never_done', label: 'Never Done' },
  { value: 'needs_completion', label: 'Needs Completion' },
  { value: 'due_soon', label: 'Due Soon' },
  { value: 'ok', label: 'OK' },
  { value: 'ignored', label: 'Ignored' }
]

const sortOptions = [
  { value: 'nextScheduledDate', label: 'Next scheduled date' },
  { value: 'siteName', label: 'Property name' },
  { value: 'address', label: 'Address' },
  { value: 'status', label: 'Status' },
  { value: 'scheduledTicketDate', label: 'Ticket date' }
]

const SubcontractorSchedule = ({ url }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState({})
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState('all')
  const [sortBy, setSortBy] = useState('nextScheduledDate')
  const [sortOrder, setSortOrder] = useState('asc')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(0)
    }, 300)

    return () => window.clearTimeout(timeoutId)
  }, [searchInput])

  const loadSchedule = useCallback(async () => {
    setLoading(true)
    setError('')

    try {
      const response = await axios.get(`${url}/api/subcontractor/schedule`, {
        params: {
          page,
          limit: rowsPerPage,
          status,
          search,
          sortBy,
          sortOrder
        }
      })

      setItems(response.data?.items || [])
      setSummary(response.data?.summary || {})
      setTotal(response.data?.total || 0)
    } catch (requestError) {
      console.error('Failed to load subcontractor schedule', requestError)
      setError(requestError?.response?.data?.message || 'Unable to load the subcontractor schedule right now.')
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, search, sortBy, sortOrder, status, url])

  useEffect(() => {
    loadSchedule()
  }, [loadSchedule])

  const summaryCards = useMemo(() => ([
    {
      eyebrow: 'Visible Items',
      value: summary.total || 0,
      caption: 'Schedule rows matching the current subcontractor filters.',
      accent: 'green'
    },
    {
      eyebrow: 'Overdue',
      value: summary.overdue || 0,
      caption: 'Properties already past the target date.',
      accent: 'red'
    },
    {
      eyebrow: 'Needs Completion',
      value: summary.needs_completion || 0,
      caption: 'Items that need closeout or additional work.',
      accent: 'plum'
    },
    {
      eyebrow: 'Due Soon',
      value: summary.due_soon || 0,
      caption: 'Upcoming work that is approaching schedule pressure.',
      accent: 'gold'
    }
  ]), [summary])

  if (loading && !items.length) {
    return <PortalLoadingCard title="Loading schedule" message="Fetching herbicide schedule items for the current subcontractor scope." minHeight={280} />
  }

  return (
    <div>
      <Grid container spacing={3}>
        {summaryCards.map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.eyebrow}>
            <PortalMetricCard
              eyebrow={card.eyebrow}
              value={card.value}
              caption={card.caption}
              accent={card.accent}
            />
          </Grid>
        ))}
      </Grid>

      <Box mt={3}>
        <Card className={classes.controlsCard}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Search properties or addresses"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Status"
                  value={status}
                  onChange={(event) => {
                    setStatus(event.target.value)
                    setPage(0)
                  }}
                  variant="outlined"
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  select
                  label="Sort by"
                  value={sortBy}
                  onChange={(event) => {
                    setSortBy(event.target.value)
                    setPage(0)
                  }}
                  variant="outlined"
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  fullWidth
                  select
                  label="Direction"
                  value={sortOrder}
                  onChange={(event) => {
                    setSortOrder(event.target.value)
                    setPage(0)
                  }}
                  variant="outlined"
                >
                  <MenuItem value="asc">Ascending</MenuItem>
                  <MenuItem value="desc">Descending</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <div className={classes.countRow}>
                  <Chip label={`Page total ${items.length}`} />
                  <Chip label={`Matching ${total}`} />
                  <Chip label={`Never done ${summary.never_done || 0}`} style={getStatusChipStyle('never_done')} />
                  <Chip label={`Ignored ${summary.ignored || 0}`} style={getStatusChipStyle('ignored')} />
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {error ? (
        <PortalStateCard
          title="Schedule unavailable"
          message={error}
          actionLabel="Retry"
          onAction={loadSchedule}
          tone="danger"
          minHeight={220}
        />
      ) : (
        <Card className={classes.tableCard}>
          <CardContent style={{ paddingBottom: 0 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gridGap={12} mb={2}>
              <div>
                <Typography variant="h6" className={classes.cardTitle}>
                  Herbicide Schedule
                </Typography>
                <Typography variant="body2" className={classes.helperText}>
                  The subcontractor API already scopes this list to the crews and territories this account can view.
                </Typography>
              </div>
              {loading ? <Chip label="Refreshing..." style={{ backgroundColor: '#eef2ff', color: '#3547a5' }} /> : null}
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.tableHeadCell}>Property</TableCell>
                    <TableCell className={classes.tableHeadCell}>Territory</TableCell>
                    <TableCell className={classes.tableHeadCell}>Next Date</TableCell>
                    <TableCell className={classes.tableHeadCell}>Last Applied</TableCell>
                    <TableCell className={classes.tableHeadCell}>Status</TableCell>
                    <TableCell className={classes.tableHeadCell}>Site Notes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item._id} hover>
                      <TableCell>
                        <Typography variant="body2" className={classes.primaryText}>
                          {item.siteName || 'Unnamed property'}
                        </Typography>
                        <Typography variant="body2" className={classes.secondaryText}>
                          {item.address || 'No address on file'}
                        </Typography>
                        {(item.communityName || item.buildingNumber) ? (
                          <Typography variant="caption" className={classes.secondaryText}>
                            {[item.communityName, item.buildingNumber].filter(Boolean).join(' • ')}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" className={classes.primaryText}>
                          {item.territoryName || 'Unassigned territory'}
                        </Typography>
                        <Typography variant="caption" className={classes.secondaryText}>
                          {item.assignedApplicator || 'No assigned applicator'}
                        </Typography>
                      </TableCell>
                      <TableCell>{formatPortalDate(item.nextScheduledDate, { includeWeekday: true })}</TableCell>
                      <TableCell>{formatPortalDate(item.lastAppliedDate, { fallback: 'No completed application recorded' })}</TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gridGap={6} alignItems="flex-start">
                          <Chip label={item.statusLabel || item.status} size="small" style={getStatusChipStyle(item.status)} />
                          {item.contractedForHerbicide ? (
                            <Chip label="Contracted" size="small" style={{ backgroundColor: '#edf4ff', color: '#23417e' }} />
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <div className={classes.noteWrap}>
                          <Typography variant="caption" className={classes.secondaryText}>
                            Gate code: {item.gateCode || 'Not provided'}
                          </Typography>
                          <Typography variant="body2" className={classes.secondaryText}>
                            {item.notes || 'No site notes attached to this job.'}
                          </Typography>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {!items.length ? (
                    <TableRow>
                      <TableCell colSpan={6} className={classes.emptyCell}>
                        <PortalStateCard
                          title="No schedule items found"
                          message={search || status !== 'all'
                            ? 'No schedule rows matched the current filters. Clear the search or broaden the status filter to see more work.'
                            : 'There are no herbicide schedule items visible to this subcontractor account yet.'}
                          minHeight={180}
                        />
                      </TableCell>
                    </TableRow>
                  ) : null}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10))
                setPage(0)
              }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </CardContent>
        </Card>
      )}
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(SubcontractorSchedule)
